import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import CustomFieldInput from "@/components/leads/CustomFieldInput";
import GroupedSelect from "@/components/leads/GroupedSelect";

/** Call Capture card (dynamic fields §8.3, catalogue options §8.4) — extracted from LeadDetail.jsx. */
export default function CaptureCard({
  form, setForm, show, req, ro, labelOf, followUpNeeded,
  empOptions, sectorOptions, dispOptions, customFields, dropdowns,
  dispositionRequired, needsDisposition, soho,
}) {
  return (
    <>
        {/* Capture — Basic fields */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Call Capture</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            {show("employee_count") && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                {labelOf("employee_count", "Employee Count")}{req("employee_count") && <span className="text-rose-500 ml-0.5">*</span>}
              </Label>
              <Select value={form.employee_count} disabled={ro("employee_count")}
                      onValueChange={(v) => setForm((f) => ({ ...f, employee_count: v }))}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="employee-count-select">
                  <SelectValue placeholder="Select bucket" />
                </SelectTrigger>
                <SelectContent>
                  {empOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            {show("sector") && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                {labelOf("sector", "Sector")}{req("sector") && <span className="text-rose-500 ml-0.5">*</span>}
              </Label>
              <Select value={form.sector} disabled={ro("sector")}
                      onValueChange={(v) => setForm((f) => ({ ...f, sector: v }))}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="sector-select">
                  <SelectValue placeholder="Select sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectorOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                {labelOf("disposition", "Disposition")}{dispositionRequired && <span className="text-rose-500 ml-0.5">*</span>}
              </Label>
              <div className={`mt-1 ${needsDisposition ? "rounded ring-1 ring-rose-300" : ""}`}>
                <GroupedSelect
                  value={form.disposition}
                  onChange={(v) => setForm((f) => ({ ...f, disposition: v }))}
                  options={dispOptions}
                  placeholder="Select disposition"
                  testid="disposition-select"
                  className={needsDisposition ? "border-rose-400" : ""}
                />
              </div>
              {needsDisposition && (
                <div className="text-[10px] text-rose-500 mt-0.5" data-testid="disposition-error">
                  Disposition is mandatory. Please select a disposition before saving the lead.
                </div>
              )}
            </div>
            <div>
              <Label className={`text-[10px] uppercase tracking-wider flex items-center gap-1 ${soho ? "text-slate-300" : "text-slate-400"}`}>
                {labelOf("follow_up_at", "Follow-up Date & Time")}
                {followUpNeeded && <span className="text-rose-500">*</span>}
              </Label>
              <Input
                type="datetime-local"
                value={form.follow_up_at ? form.follow_up_at.slice(0, 16) : ""}
                onChange={(e) => setForm((f) => ({ ...f, follow_up_at: e.target.value }))}
                disabled={soho}
                title={soho ? "Not available for Ooredoo SOHO" : undefined}
                className={`h-8 mt-1 text-xs ${soho ? "bg-slate-100 text-slate-400 cursor-not-allowed" : ""}`}
                data-testid="follow-up-input"
              />
              {soho && (
                <div className="text-[10px] text-slate-400 mt-0.5" data-testid="follow-up-disabled-hint">
                  Not available for Ooredoo SOHO
                </div>
              )}
              {!soho && followUpNeeded && !form.follow_up_at && (
                <div className="text-[10px] text-rose-500 mt-0.5">Required for disposition {form.disposition}</div>
              )}
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Call Language</Label>
              <RadioGroup
                value={form.call_language}
                onValueChange={(v) => setForm((f) => ({ ...f, call_language: v }))}
                className="flex gap-4 mt-1"
              >
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <RadioGroupItem value="english" data-testid="call-lang-english" /> English
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <RadioGroupItem value="arabic_only" data-testid="call-lang-arabic" /> Arabic Only
                </label>
              </RadioGroup>
              {form.call_language === "arabic_only" && (
                <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5 mt-1.5">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Saving will trigger reassignment to an Arabic-speaking agent (Phase 4).
                </div>
              )}
            </div>
            {show("remarks") && (
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                {labelOf("remarks", "Remarks")}{req("remarks") && <span className="text-rose-500 ml-0.5">*</span>}
              </Label>
              <Textarea
                value={form.remarks}
                disabled={ro("remarks")}
                onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                rows={3}
                className="mt-1 text-xs resize-none"
                data-testid="remarks-input"
                placeholder="Additional notes on this interaction"
              />
            </div>
            )}
            {customFields.filter((f) => f.section === "capture").map((f) => (
              <CustomFieldInput key={f.id} field={f} value={(form.custom || {})[f.key] || ""}
                dropdowns={dropdowns}
                freeText={soho && f.dropdown_key === "area"}
                onChange={(v) => setForm((prev) => ({ ...prev, custom: { ...(prev.custom || {}), [f.key]: v } }))} />
            ))}
          </CardContent>
        </Card>
    </>
  );
}
